import { Component, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

/**
 * The boundary that keeps one broken screen from taking the application with it.
 *
 * React unmounts the whole tree when a render throws and nothing catches it, so a
 * single field read against a response shape that changed blanks the window — no
 * navigation, no way back, nothing on screen to report. For a system somebody runs
 * their working day in, losing the shell is a much worse failure than losing one
 * panel, so every route renders inside this.
 *
 * It deliberately does not retry by itself. A render that threw once will throw again
 * on the same data, and an automatic remount produces a flicker loop rather than a
 * recovery. The user gets the shell back, the technical detail to quote, and two
 * explicit ways out.
 */

interface Props {
  children: ReactNode;
  /** Changing this remounts the subtree — the route path, so navigating away recovers. */
  zuruecksetzenBei?: string;
}

interface State {
  fehler: Error | null;
  /** Component stack, kept for the detail disclosure rather than the console alone. */
  stapel: string | null;
}

class Grenze extends Component<Props, State> {
  override state: State = { fehler: null, stapel: null };

  static getDerivedStateFromError(fehler: Error): Partial<State> {
    return { fehler };
  }

  override componentDidUpdate(vorher: Props): void {
    // Navigating to another route is the natural "get me out of here", so recover
    // silently rather than stranding the user on the error panel.
    if (this.state.fehler && vorher.zuruecksetzenBei !== this.props.zuruecksetzenBei) {
      this.setState({ fehler: null, stapel: null });
    }
  }

  override componentDidCatch(fehler: Error, info: ErrorInfo): void {
    this.setState({ stapel: info.componentStack ?? null });
    // Kept as console.error on purpose: it is what a developer opening the console
    // during a demo will look for, and it is where an error reporter hooks in.
    console.error("Unbehandelter Fehler in der Oberfläche", fehler, info.componentStack);
  }

  private erneutVersuchen = (): void => {
    this.setState({ fehler: null, stapel: null });
  };

  override render(): ReactNode {
    const { fehler, stapel } = this.state;
    if (!fehler) return this.props.children;

    return (
      <div className="p-[16px]">
        <div
          role="alert"
          className="border max-w-[68ch]"
          style={{
            background: "var(--surface-raised)",
            borderColor: "var(--status-bad-bar)",
          }}
        >
          <div
            className="px-[12px] py-[8px] border-b text-2xs font-medium"
            style={{
              background: "var(--status-bad-bg)",
              borderColor: "var(--status-bad-bar)",
              color: "var(--status-bad-tx)",
              letterSpacing: "var(--tracking-caps)",
              textTransform: "uppercase",
            }}
          >
            Diese Ansicht konnte nicht dargestellt werden
          </div>

          <div className="px-[12px] py-[12px] flex flex-col gap-[12px]">
            <p
              className="text-sm"
              style={{ color: "var(--text-primary)", lineHeight: "var(--leading-text)" }}
            >
              Die übrigen Bereiche funktionieren weiter. Ihre Daten sind nicht betroffen —
              die Oberfläche konnte lediglich nicht gezeichnet werden.
            </p>

            <p className="text-xs mono" style={{ color: "var(--text-secondary)" }}>
              {fehler.message || fehler.name}
            </p>

            <div className="flex gap-[8px]">
              <button
                type="button"
                onClick={this.erneutVersuchen}
                className="text-sm px-[12px] py-[5px] rounded-2"
                style={{
                  background: "var(--accent-solid)",
                  color: "var(--accent-on-solid)",
                }}
              >
                Erneut zeichnen
              </button>
              <button
                type="button"
                onClick={() => window.location.assign("/")}
                className="text-sm px-[12px] py-[5px] rounded-2 border"
                style={{
                  background: "var(--surface-chrome)",
                  borderColor: "var(--line-strong)",
                  color: "var(--text-primary)",
                }}
              >
                Zum Periodenlauf
              </button>
            </div>

            {stapel ? (
              <details>
                <summary
                  className="text-2xs cursor-pointer"
                  style={{ color: "var(--text-muted)" }}
                >
                  Technische Angaben
                </summary>
                <pre
                  className="text-2xs mono mt-[6px] overflow-x-auto p-[8px]"
                  style={{
                    background: "var(--surface-sunken)",
                    color: "var(--text-secondary)",
                  }}
                >
                  {stapel.trim()}
                </pre>
              </details>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}

/** Route-scoped boundary: recovers on navigation without the caller wiring that up. */
export function Fehlergrenze({ children }: { children: ReactNode }): ReactNode {
  const ort = useLocation();
  return <Grenze zuruecksetzenBei={ort.pathname}>{children}</Grenze>;
}

/** Outermost boundary, above the router — nothing above it can recover the shell. */
export function WurzelFehlergrenze({ children }: { children: ReactNode }): ReactNode {
  return <Grenze>{children}</Grenze>;
}
